import { Dispatch, SetStateAction, useState } from 'react';

import { NATIVE_TOKEN_SYMBOL_PER_CHAIN } from '@/constants/chain';

import { autoBannerSelectors, useAppStore } from '@/store/app';
import { gaslessSelectors, useMarketTradeSettingsStore } from '@/store/tradeSettings/market';

import { buttons } from '@/styles/buttons';
import { fontStyles } from '@/styles/typography';

import { MatchaGreenInvertedIcon } from '@/ui/Icons/MatchaGreenInvertedIcon';
import { XIcon } from '@/ui/Icons/XIcon';

import animations from './animations.module.css';

interface MatchaAutoBannerProps {
  /**  Whether the banner is visible. */
  setAutoBannerVisible: Dispatch<SetStateAction<boolean>>;
  /** The chain ID of the current network. */
  chainId: number;
  /** Whether the banner is being displayed on a mobile device. */
  isMobile?: boolean;
}

const BANNER_ANIMATION_CLOSE_DURATION = 100;
const MONTHS_TO_HIDE = 1;

export const MatchaAutoBanner = ({
  chainId,
  isMobile,
  setAutoBannerVisible,
}: MatchaAutoBannerProps) => {
  // state
  const { setAutoBannerDismissedUntil } = useAppStore(autoBannerSelectors);
  const { setIsGasless } = useMarketTradeSettingsStore(gaslessSelectors);
  const [isExiting, setIsExiting] = useState<boolean>(false);

  // vars
  const getDismissDate = () => {
    const now = new Date();
    return now.setMonth(now.getMonth() + MONTHS_TO_HIDE);
  };

  const dismiss = () => {
    setAutoBannerDismissedUntil(getDismissDate());
    setAutoBannerVisible(false);
  };

  // handlers
  const handleClickDismiss = () => {
    setIsExiting(true);
    setTimeout(() => dismiss(), BANNER_ANIMATION_CLOSE_DURATION);
  };

  const handleClickEnable = () => {
    setIsExiting(true);
    setTimeout(() => {
      setIsGasless(true);
      dismiss();
    }, BANNER_ANIMATION_CLOSE_DURATION);
  };

  return (
    <div
      className={`bg-special-background-card border border-special-border-card rounded-lg mt-4 p-5 overflow-hidden ${
        isExiting ? animations['animate-popover-out'] : animations['animate-popover-in']
      } ${isMobile ? 'mx-auto mb-80 shadow-30 max-w-[90%]' : ''}`}
    >
      <div className="flex items-center justify-between w-full">
        <h3
          className={fontStyles({
            variant: 'BODY_LG',
            class: ['text-token-green-1100', 'font-medium'],
          })}
          style={{ textShadow: '0 0 40px rgb(44, 176, 81)' }}
        >
          <MatchaGreenInvertedIcon className="mr-6 align-middle" />
          No {NATIVE_TOKEN_SYMBOL_PER_CHAIN[chainId]}? No Problem!
        </h3>

        <button
          className={buttons({
            size: 'small',
            color: 'gray',
            shape: 'rounded',
          })}
          onClick={handleClickDismiss}
        >
          <XIcon className="h-4 w-4" />
        </button>
      </div>

      <p className={fontStyles({ variant: 'BODY_SM', class: ['my-5'] })}>
        Trade without holding native tokens. Matcha Auto will handle the network cost for you.
      </p>

      <button
        className={buttons({
          size: 'small',
          color: 'gray',
          shape: 'rounded',
        })}
        onClick={handleClickEnable}
      >
        Enable Matcha Auto
      </button>
    </div>
  );
};
